/* Speedy Food — Hero Showcase v4 ("True Orbital Wheel").
   - Le 3 slide vivono SEMPRE sull'orbita circolare.
   - JS interpola wheelRotation continuamente via requestAnimationFrame.
   - Posizione/scale/opacity di ogni slide calcolate dal sin/cos dell'angolo visibile.
   - Nessuna classe "is-exit"/"is-enter": tutto fluido, nessun teleport.
   - Desktop: orbita verticale (asse X). Mobile: orbita orizzontale (asse Y).
   - L'exiting slide rimane semi-trasparente per "ghost continuation".
   - Auto-rotate 5s, pause on hover + 12s post-interaction.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-hero-showcase]');
  if (!root) return;

  var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
  var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
  var satellites = Array.prototype.slice.call(root.querySelectorAll('[data-hero-satellite]'));
  var ingredientSets = Array.prototype.slice.call(root.querySelectorAll('.hero-ingredient-set'));
  var slotItems = {
    eyebrow: Array.prototype.slice.call(root.querySelectorAll('[data-hero-slot="eyebrow"] .hero-slot-item')),
    headline: Array.prototype.slice.call(root.querySelectorAll('[data-hero-slot="headline"] .hero-slot-item')),
    quote: Array.prototype.slice.call(root.querySelectorAll('[data-hero-slot="quote"] .hero-slot-item')),
  };
  var counterEl = root.querySelector('[data-hero-counter]');
  var stageEl = root.querySelector('[data-hero-stage]');

  var total = slides.length;
  if (total < 2) return;

  // ───── State ─────
  var ANGLE_STEP = 360 / total;     // 120° per slide
  var currentRotation = 0;          // CSS-units degrees, decreases as we advance
  var currentIndex = 0;
  var pendingIndex = null;          // target of in-flight transition; null when idle
  var animating = false;
  var rafId = null;
  var pausedUntil = 0;
  var autoplayTimer = null;
  var hovering = false;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  var isMobile = window.matchMedia('(max-width: 960px)').matches;

  var AUTOPLAY_MS = 5000;
  var PAUSE_AFTER_INTERACTION_MS = 12000;
  var TRANSITION_MS = 1200;

  // ───── Geometry — RAW TRIG PRIMITIVES ─────
  // Emette sin(v) e cos(v) grezzi. Il CSS sceglie come comporli per breakpoint:
  //   Desktop (S-curve orbit):   X = (1 - cos) * x-radius, Y = sin * y-radius
  //   Mobile (linear slide):     X = sin * x-radius, Y = 0
  // Depth (translateZ) = cos in entrambi.
  function computeSlideLayout(visibleAngle) {
    var a = ((visibleAngle % 360) + 540) % 360 - 180;
    var rad = a * Math.PI / 180;
    var absA = Math.abs(a);

    var sinV = Math.sin(rad);
    var cosV = Math.cos(rad);

    // Scale: front (cos=+1) → 1.0; back (cos=-1) → 0.32
    var scale = 0.32 + 0.68 * ((cosV + 1) / 2);

    // Opacity curve: active opaque, satellites visibly present at ±120° rest,
    // fading only when going behind (>140°).
    var opacity;
    if (absA <= 60) {
      opacity = 1;
    } else if (absA <= 125) {
      opacity = 1 - ((absA - 60) / 65) * 0.25;  // 1 → 0.75 (satellite rest at 120° ≈ 0.77)
    } else if (absA <= 165) {
      opacity = 0.75 - ((absA - 125) / 40) * 0.75;  // 0.75 → 0
    } else {
      opacity = 0;
    }

    return {
      angle: a,
      sin: sinV,
      cos: cosV,
      scale: scale,
      opacity: opacity,
    };
  }

  function applyLayout(slide, layout) {
    // Raw trig primitives — CSS deriva il percorso effettivo per breakpoint
    slide.style.setProperty('--orbit-sin', layout.sin.toFixed(4));
    slide.style.setProperty('--orbit-cos', layout.cos.toFixed(4));
    // Cached compositions (back-compat con codice esistente)
    slide.style.setProperty('--orbit-translate-x-ratio', (1 - layout.cos).toFixed(4));
    slide.style.setProperty('--orbit-translate-y-ratio', layout.sin.toFixed(4));
    slide.style.setProperty('--orbit-depth-ratio', layout.cos.toFixed(4));
    slide.style.setProperty('--orbit-scale', layout.scale.toFixed(4));
    slide.style.setProperty('--orbit-opacity', layout.opacity.toFixed(4));
    slide.style.setProperty('--orbit-z-index', Math.round((layout.cos + 1) * 50));
    slide.setAttribute('aria-hidden', layout.opacity > 0.7 ? 'false' : 'true');
  }

  function updateAllPositions(rotation) {
    slides.forEach(function (slide, i) {
      var plateAngleOnWheel = i * ANGLE_STEP;
      var visibleAngle = plateAngleOnWheel + rotation;
      var layout = computeSlideLayout(visibleAngle);
      applyLayout(slide, layout);
    });
  }

  // ───── Text + UI sync ─────
  function syncUI(index) {
    // Dots
    dots.forEach(function (d, i) {
      d.classList.toggle('is-active', i === index);
      d.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    // Satellites — hide active satellite, show others
    satellites.forEach(function (sat, i) {
      sat.classList.toggle('is-active', i === index);
    });
    // Ingredient sets
    ingredientSets.forEach(function (set, i) {
      set.classList.toggle('is-active', i === index);
    });
    // Text slots
    Object.keys(slotItems).forEach(function (key) {
      slotItems[key].forEach(function (item, i) {
        item.classList.toggle('is-active', i === index);
      });
    });
    // Counter
    if (counterEl) {
      var n = String(index + 1);
      if (n.length < 2) n = '0' + n;
      var d = String(total);
      if (d.length < 2) d = '0' + d;
      counterEl.textContent = n + ' / ' + d;
    }
  }

  // ───── Transition ─────
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Each transition gets a unique token; only the most recent token's step
  // is allowed to mutate state. New calls cancel any in-flight transition.
  var transitionToken = 0;

  function transitionTo(targetIndex) {
    // Already at target and not in motion → no-op.
    if (targetIndex === currentIndex && !animating && pendingIndex === null) return;

    // ABSOLUTE target rotation: the value congruent to −targetIndex·ANGLE_STEP
    // (mod 360) nearest to the rotation currently on screen. Interruption-safe
    // by construction: whatever mid-flight rotation we start from, the wheel
    // always lands exactly on the slot the text/UI is announcing.
    var targetBase = -targetIndex * ANGLE_STEP;
    var wraps = Math.round((currentRotation - targetBase) / 360);
    var targetRotation = targetBase + wraps * 360;

    pendingIndex = targetIndex;
    animating = true;

    syncUI(targetIndex);

    var startRotation = currentRotation;
    var startTime = performance.now();
    var token = ++transitionToken;

    if (rafId) cancelAnimationFrame(rafId);

    function step(now) {
      // Stale frame (another transition took over) → abort silently.
      if (token !== transitionToken) return;

      var elapsed = now - startTime;
      var t = Math.min(1, elapsed / TRANSITION_MS);
      var eased = reduced ? t : easeInOutCubic(t);
      currentRotation = startRotation + (targetRotation - startRotation) * eased;
      updateAllPositions(currentRotation);

      if (t < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        rafId = null;
        animating = false;
        currentIndex = targetIndex;
        pendingIndex = null;
      }
    }

    rafId = requestAnimationFrame(step);
  }

  // The index the UI is committed to right now (pending target wins while a
  // transition is in flight) — prevents stale-index math on interruption.
  function activeIndex() {
    return pendingIndex !== null ? pendingIndex : currentIndex;
  }

  function go(index) {
    var idx = ((index % total) + total) % total;
    transitionTo(idx);
    pausedUntil = Date.now() + PAUSE_AFTER_INTERACTION_MS;
  }

  function next() {
    var idx = (activeIndex() + 1) % total;
    transitionTo(idx);
  }

  // ───── Autoplay ─────
  function startAutoplay() {
    stopAutoplay();
    if (reduced) return;
    autoplayTimer = setTimeout(function tick() {
      if (hovering || Date.now() < pausedUntil) {
        autoplayTimer = setTimeout(tick, 500);
        return;
      }
      next();
      autoplayTimer = setTimeout(tick, AUTOPLAY_MS);
    }, AUTOPLAY_MS);
  }
  function stopAutoplay() {
    if (autoplayTimer) {
      clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  }

  // ───── Interactions ─────
  dots.forEach(function (dot) {
    dot.addEventListener('click', function () {
      var idx = parseInt(dot.getAttribute('data-hero-dot'), 10);
      go(idx);
    });
  });

  satellites.forEach(function (sat) {
    sat.addEventListener('click', function () {
      var idx = parseInt(sat.getAttribute('data-hero-satellite'), 10);
      go(idx);
    });
  });

  // Clicking a slide that's NOT active = navigate to it
  slides.forEach(function (slide, i) {
    slide.addEventListener('click', function () {
      if (i !== activeIndex()) go(i);
    });
  });

  root.addEventListener('mouseenter', function () { hovering = true; });
  root.addEventListener('mouseleave', function () { hovering = false; });

  // Touch swipe (mobile)
  if (coarsePointer && stageEl) {
    var touchStartX = 0, touchStartY = 0, touchActive = false;
    stageEl.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchActive = true;
    }, { passive: true });
    stageEl.addEventListener('touchend', function (e) {
      if (!touchActive) return;
      touchActive = false;
      var endTouch = e.changedTouches[0];
      var deltaX = endTouch.clientX - touchStartX;
      var deltaY = Math.abs(endTouch.clientY - touchStartY);
      if (Math.abs(deltaX) > 50 && deltaY < 80) {
        if (deltaX < 0) next();
        else go((activeIndex() - 1 + total) % total);
      }
    }, { passive: true });
  }

  // Mouse parallax (desktop only)
  if (!reduced && !coarsePointer && stageEl) {
    var rafPending = false;
    var mx = 0, my = 0;
    stageEl.addEventListener('mousemove', function (e) {
      var rect = stageEl.getBoundingClientRect();
      mx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      my = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      if (!rafPending) {
        rafPending = true;
        window.requestAnimationFrame(function () {
          rafPending = false;
          stageEl.style.setProperty('--parallax-x', (mx * 8).toFixed(2) + 'px');
          stageEl.style.setProperty('--parallax-y', (my * 8).toFixed(2) + 'px');
        });
      }
    });
    stageEl.addEventListener('mouseleave', function () {
      mx = 0; my = 0;
      stageEl.style.setProperty('--parallax-x', '0px');
      stageEl.style.setProperty('--parallax-y', '0px');
    });
  }

  // Resize listener — update isMobile flag (axis decision in CSS)
  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (resizeRaf) return;
    resizeRaf = window.requestAnimationFrame(function () {
      resizeRaf = null;
      isMobile = window.matchMedia('(max-width: 960px)').matches;
      updateAllPositions(currentRotation);
    });
  });

  // Pause autoplay when out of viewport
  if ('IntersectionObserver' in window) {
    var visObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) startAutoplay();
        else stopAutoplay();
      });
    }, { threshold: 0.1 });
    visObs.observe(root);
  } else {
    startAutoplay();
  }

  // Initial state — position all slides correctly
  syncUI(0);
  updateAllPositions(0);
})();
